import {
  Button,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { IconType } from "react-icons";
import linkIcon from "../assets/reviewIcons/link.svg";

export const CREATIVE_IDENTITY_REVIEW_ROW_ICON_SIZE = 6;
export const CREATIVE_IDENTITY_REVIEW_ROW_LABEL_MIN_W = "120px";

export type CreativeIdentityReviewRow = {
  key: string;
  icon: IconType | "link";
  label: string;
  value: string;
};

function IdentityReviewRowIcon({
  icon,
}: {
  icon: CreativeIdentityReviewRow["icon"];
}) {
  if (icon === "link") {
    return (
      <Image
        src={linkIcon}
        alt=""
        boxSize={CREATIVE_IDENTITY_REVIEW_ROW_ICON_SIZE}
        flexShrink={0}
      />
    );
  }
  return (
    <Icon
      as={icon}
      boxSize={CREATIVE_IDENTITY_REVIEW_ROW_ICON_SIZE}
      color="gray.500"
      flexShrink={0}
    />
  );
}

export type CreativeIdentityReviewRowsProps = {
  rows: CreativeIdentityReviewRow[];
  onEditAction?: () => void;
  editActionLabel?: string;
  editDisabled?: boolean;
  /** When false, omits the top border (caller may use Divider). */
  showTopBorder?: boolean;
};

export default function CreativeIdentityReviewRows({
  rows,
  onEditAction,
  editActionLabel = "Change",
  editDisabled = false,
  showTopBorder = true,
}: CreativeIdentityReviewRowsProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <VStack
      align="stretch"
      spacing={1.5}
      pt={showTopBorder ? 2 : 0}
      borderTop={showTopBorder ? "1px solid" : undefined}
      borderColor="gray.200"
    >
      {rows.map(({ key, icon, label, value }) => (
        <HStack
          key={key}
          align="center"
          w="full"
          spacing={1}
          justify="space-between"
        >
          <HStack
            minW={CREATIVE_IDENTITY_REVIEW_ROW_LABEL_MIN_W}
            spacing={1.5}
            align="center"
            flexShrink={0}
          >
            <IdentityReviewRowIcon icon={icon} />
            <Text fontSize="sm" fontWeight="semibold" color="gray.600">
              {label}
            </Text>
          </HStack>
          <Text
            flex={1}
            minW={0}
            fontSize="sm"
            fontWeight="medium"
            color="gray.600"
            noOfLines={1}
          >
            {value}
          </Text>
          {onEditAction ? (
            <Button
              variant="link"
              fontSize="sm"
              fontWeight="normal"
              color="blue.600"
              flexShrink={0}
              onClick={() => !editDisabled && onEditAction()}
              cursor="pointer"
              isDisabled={editDisabled}
              textDecoration="underline"
              _hover={{ textDecoration: "underline" }}
            >
              {editActionLabel}
            </Button>
          ) : null}
        </HStack>
      ))}
    </VStack>
  );
}
